import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
// eslint-disable-next-line no-restricted-imports
import { CarlaChatPane } from '@/modules/carla/components/CarlaChatPane'
// eslint-disable-next-line no-restricted-imports
import { useConversationMessages } from '@/modules/carla/api/queries'
// CARLA chat lives in @/modules/carla/ today; lifting it to
// @/shell/services/carla-chat/ is a queued cleanup. Until then this
// embed needs to reach across module boundaries — contained to this file.
import { useIntakeConversation } from '../api/queries'
import type { IntakeDetail } from '../api/types'

export interface SalesIntakeChatPanelProps {
  intake: IntakeDetail
  /** Auth user role — drives can_confirm_actions. Mirrors carla page. */
  canConfirmActions: boolean
}

/**
 * Right-side panel of the sales-intake detail drawer. Hosts a CARLA
 * conversation scoped to this specific intake. CARLA's tool calls
 * (update_sales_order_intake, etc.) modify the intake; the parent
 * drawer's react-query invalidation re-fetches and the line grid
 * shows the changes live.
 */
export function SalesIntakeChatPanel({
  intake,
  canConfirmActions,
}: SalesIntakeChatPanelProps) {
  const conversation = useIntakeConversation(intake.id)
  const conversationId = conversation.data?.conversation_id ?? null
  const qc = useQueryClient()

  // Load the canonical thread's history so CARLA's prior review (and any
  // earlier operator turns) persist across card reopens — mirrors CarlaPage.
  const messagesQuery = useConversationMessages(conversationId)
  const seedHistory = useMemo(
    () => messagesQuery.data?.messages ?? [],
    [messagesQuery.data],
  )

  // Build a fresh odoo_context every send. The hook calls this via a
  // getOdooContext callback so it always sees the latest intake state,
  // not the value at chat-mount time.
  const odooContextBuilder = useMemo(
    () => () => ({
      panel: 'sales_intake' as const,
      intake_id: intake.id,
      customer_id: intake.customer_id,
      customer_name: intake.customer_name,
      customer_po_number: intake.customer_po_number,
      requested_delivery_date: intake.requested_delivery_date,
      line_count: intake.line_count,
      status: intake.status,
      carla_directive: [
        `You're embedded in the /sales-inbox panel for intake ${intake.id}.`,
        `Use update_sales_order_intake to apply edits — DO NOT ask the user`,
        `for the intake_id, it's ${intake.id}. Operator can see your tool`,
        `calls land live in the drawer, so be concise. Only call`,
        `confirm_sales_order_intake when the operator explicitly says`,
        `"send to Odoo" or "confirm" — otherwise prepare edits, never confirm.`,
      ].join(' '),
    }),
    [
      intake.id,
      intake.customer_id,
      intake.customer_name,
      intake.customer_po_number,
      intake.requested_delivery_date,
      intake.line_count,
      intake.status,
    ],
  )

  // After CARLA streams a response, refetch the intake detail so any
  // tool-call edits land in the line grid.
  function handleMessageSent() {
    qc.invalidateQueries({ queryKey: ['sales-inbox', 'detail', intake.id] })
    qc.invalidateQueries({ queryKey: ['sales-inbox', 'list'] })
    // Refresh the persisted conversation history so a reopen shows the
    // turn that just streamed.
    if (conversationId) {
      qc.invalidateQueries({
        queryKey: ['carla', 'conversations', conversationId, 'messages'],
      })
    }
  }

  // Scroll-trap fix: drawer's split column has overflow-y-auto; the chat
  // pane assumes its parent provides height. Wrap in a column flex.
  if (conversation.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tertiary">
        Loading chat…
      </div>
    )
  }
  if (conversation.error || !conversationId) {
    return (
      <div className="p-4 text-sm text-danger">
        Failed to load chat thread.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col min-h-0">
      <header className="flex items-center gap-1.5 border-b border-border-subtle bg-surface px-4 py-2">
        <Sparkles size={14} className="text-info" />
        <span className="text-role-section-label m-0">CARLA</span>
        <span className="text-xs text-tertiary ml-2">
          ask, refine, send to Odoo
        </span>
      </header>
      <div className="flex-1 min-h-0 flex flex-col">
        <CarlaChatPane
          conversationId={conversationId}
          seedHistory={seedHistory}
          isLoadingSeed={messagesQuery.isLoading}
          canConfirmActions={canConfirmActions}
          onMessageSent={handleMessageSent}
          odooContext={odooContextBuilder}
        />
      </div>
    </div>
  )
}
